import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthenticationService } from '../auth/authentication.service';
import {
  AccountPick,
  CorrespondenceAlert,
  CorrespondenceAudience,
  CorrespondenceEmail,
  CorrespondenceFiles,
  CorrespondenceTemplate,
  CorrespondenceTemplateRequest,
  SendCorrespondenceRequest,
} from '../../models/correspondence.model';

@Injectable({ providedIn: 'root' })
export class CorrespondenceService {
  private readonly baseUrl = `${environment.apiUrl}/api/correspondence`;

  constructor(private readonly http: HttpClient, private readonly auth: AuthenticationService) {}

  getTokens(): Observable<string[]> {
    return this.http.get<string[]>(`${this.baseUrl}/tokens`);
  }

  getTemplates(): Observable<CorrespondenceTemplate[]> {
    return this.http.get<CorrespondenceTemplate[]>(`${this.baseUrl}/templates`);
  }

  getTemplate(id: number): Observable<CorrespondenceTemplate> {
    return this.http.get<CorrespondenceTemplate>(`${this.baseUrl}/templates/${id}`);
  }

  saveTemplate(id: number | null, request: CorrespondenceTemplateRequest, files: CorrespondenceFiles): Observable<CorrespondenceTemplate> {
    const body = this.multipart({ ...request, keepAttachmentIds: files.keepIds }, files);
    return id
      ? this.http.put<CorrespondenceTemplate>(`${this.baseUrl}/templates/${id}`, body, { headers: this.userHeaders() })
      : this.http.post<CorrespondenceTemplate>(`${this.baseUrl}/templates`, body, { headers: this.userHeaders() });
  }

  countAudience(audience: CorrespondenceAudience): Observable<{ count: number }> {
    return this.http.post<{ count: number }>(`${this.baseUrl}/audience/count`, audience);
  }

  searchAccounts(q: string): Observable<AccountPick[]> {
    return this.http.get<AccountPick[]>(`${this.baseUrl}/accounts`, { params: { q } });
  }

  getEmails(): Observable<CorrespondenceEmail[]> {
    return this.http.get<CorrespondenceEmail[]>(`${this.baseUrl}/emails`);
  }

  send(request: SendCorrespondenceRequest, files: CorrespondenceFiles): Observable<CorrespondenceEmail> {
    const body = this.multipart({ ...request, templateAttachmentIds: files.keepIds }, files);
    return this.http.post<CorrespondenceEmail>(`${this.baseUrl}/send`, body, { headers: this.userHeaders() });
  }

  getAlerts(): Observable<CorrespondenceAlert[]> {
    return this.http.get<CorrespondenceAlert[]>(`${this.baseUrl}/alerts`);
  }

  toggleAlertEnabled(alertId: number, enabled: boolean): Observable<CorrespondenceAlert> {
    return this.http.put<CorrespondenceAlert>(`${this.baseUrl}/alerts/${alertId}`, { enabled });
  }

  /** JSON part "request" + attachments[] + inlineFiles[]/inlineCids[] (parallel). */
  private multipart(request: unknown, files: CorrespondenceFiles): FormData {
    const fd = new FormData();
    fd.append('request', new Blob([JSON.stringify(request)], { type: 'application/json' }));
    files.attachments.forEach((f) => fd.append('attachments', f, f.name));
    files.inline.forEach((f) => {
      fd.append('inlineFiles', f.file, f.file.name);
      fd.append('inlineCids', f.cid ?? '');
    });
    return fd;
  }

  private userHeaders(): HttpHeaders | undefined {
    const name = this.auth.getCurrentUser()?.username;
    return name ? new HttpHeaders({ 'X-User-Name': String(name) }) : undefined;
  }
}
